import { escapeHtml, formatCprForPrint } from '@/lib/referral/referralPrint';
import { mergeGgopStylesheets } from '@/lib/ggop/ggopView';

// Spor A block 3 (XDIS-EPJ 3.3, 7.x, 4.x): XDIS16, XDIS18 and XDIS21
// are shown with MedCom's own stylesheet
// (Referencer/XDIS/_Faelles/html_1.0.3: viewEmessage.xsl and the six files it includes;
// XSLT 1.0, run by the browser's XSLTProcessor), so everything in MedCom's html view is
// found in Clinio. The includes are merged into one stylesheet with the same merge the
// GGOP view uses (browsers do not reliably resolve xsl:include). The XML comes from
// communicate-xdis with the patient's CPR replaced by XDIS_CPR_MARKER (put back only after
// an audited decrypt-cpr) and any other person's CPR by XDIS_OTHER_CPR_MARKER (never shown).

export const XDIS_XSL_BASE = '/medcom/xdis/';
export const XDIS_XSL_MAIN = 'viewEmessage.xsl';
export const XDIS_XSL_FILES = [
  'viewEmessage.xsl',
  'common.xsl',
  'reportofdischarge2html.xsl',
  'reportofadmission2html.xsl',
  'notificationffrndedtreatment2html.xsl',
  'progressofcareplan2html.xsl',
  'rehabilitationplan2html.xsl',
] as const;
export const XDIS_CPR_MARKER = '##CPR##';
export const XDIS_OTHER_CPR_MARKER = '##CPR-SKJULT##';

let cached: Promise<string> | null = null;

/** The merged XDIS stylesheet, fetched once from the app's own /medcom/xdis/. */
export function loadXdisStylesheet(fetcher: typeof fetch = fetch): Promise<string> {
  if (!cached) {
    cached = Promise.all(
      XDIS_XSL_FILES.map(async (name) => {
        const res = await fetcher(`${XDIS_XSL_BASE}${name}`);
        if (!res.ok) throw new Error(`Kunne ikke hente ${name}`);
        return [name, await res.text()] as const;
      }),
    ).then((pairs) => mergeGgopStylesheets(Object.fromEntries(pairs), XDIS_XSL_MAIN));
    cached.catch(() => {
      cached = null;
    });
  }
  return cached;
}

/** The XML with the patient's CPR marker replaced (ddmmåå-xxxx or a placeholder) and other CPRs hidden. */
export function withXdisCpr(xml: string, cpr: string | null, placeholder: string, hidden: string): string {
  const value = cpr ? formatCprForPrint(cpr) : placeholder;
  return xml.split(XDIS_OTHER_CPR_MARKER).join(escapeHtml(hidden)).split(XDIS_CPR_MARKER).join(escapeHtml(value));
}

export const XDIS_HEADINGS: Record<string, string> = {
  XDIS16: 'Indlæggelsesrapport (XDIS16)',
  XDIS18: 'Udskrivningsrapport (XDIS18)',
  XDIS21: 'Plejeforløbsplan (XDIS21)',
};

export function xdisHeading(kind: string | null | undefined): string {
  return (kind && XDIS_HEADINGS[kind]) || 'Sygehus-kommune meddelelse';
}

/** "Version n af m · Opdateret" for an XDIS16 of an episode (XDIS-EPJ 3.6/3.7). */
export function xdis16VersionText(row: { version: number; versions: number; updated: boolean }): string {
  if (row.versions <= 1) return '';
  return `Version ${row.version} af ${row.versions}${row.updated ? ' · Opdateret' : ''}`;
}

function xdisDateMs(at: string | null | undefined): number {
  const raw = at?.trim() ?? '';
  if (!raw) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(raw.includes('T') ? raw : raw.replace(' ', 'T'));
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
}

/** Newest XDIS16 exchange id by date. Rows with `kind` other than XDIS16 are ignored. */
export function latestXdis16ExchangeId(
  rows: readonly { exchangeId: string; at?: string | null; kind?: string }[],
): string | null {
  let best: { exchangeId: string; ms: number } | null = null;
  for (const row of rows) {
    if (!row.exchangeId || (row.kind !== undefined && row.kind !== 'XDIS16')) continue;
    const ms = xdisDateMs(row.at);
    if (!best || ms > best.ms) best = { exchangeId: row.exchangeId, ms };
  }
  return best?.exchangeId ?? null;
}
